<script>
  import ReceiptCard from '../components/receipt/ReceiptCard.svelte';
  import ReceiptPageLayout from '../components/receipt/ReceiptPageLayout.svelte';
  import ReceiptHeader from '../components/receipt/ReceiptHeader.svelte';
  import ReceiptLabel from '../components/receipt/ReceiptLabel.svelte';
  import ReceiptDivider from '../components/receipt/ReceiptDivider.svelte';
  import ReceiptItemRow from '../components/receipt/ReceiptItemRow.svelte';
  import ReceiptFooter from '../components/receipt/ReceiptFooter.svelte';
  import ReceiptStamp from '../components/receipt/ReceiptStamp.svelte';

  let {
    trip = {
      id: 'trip-001',
      name: '도쿄 벚꽃 여행',
      destination: 'TOKYO',
    },
    settlement = {
      totalExpenses: 4500000,
      perPersonShare: 1125000,
      settled: true,
      settledAt: '2026-03-26',
      participants: [
        { user: { id: '1', name: '김승한' }, paid: 2000000, owed: 1125000, balance: 875000 },
        { user: { id: '2', name: '이지원' }, paid: 1500000, owed: 1125000, balance: 375000 },
        { user: { id: '3', name: '박민호' }, paid: 500000, owed: 1125000, balance: -625000 },
        { user: { id: '4', name: '최유나' }, paid: 500000, owed: 1125000, balance: -625000 },
      ],
      transactions: [
        { from: { name: '박민호' }, to: { name: '김승한' }, amount: 625000 },
        { from: { name: '최유나' }, to: { name: '이지원' }, amount: 375000 },
        { from: { name: '최유나' }, to: { name: '김승한' }, amount: 250000 },
      ],
    },
  } = $props();

  let participantCount = $derived(settlement.participants.length);
  let today = $derived(new Date().toISOString().split('T')[0]);

  function toDisplay(amount) {
    return Math.round(amount / 100);
  }

  function fmtAmount(amount) {
    return toDisplay(amount).toLocaleString();
  }

  function fmtBalance(balance) {
    const formatted = fmtAmount(Math.abs(balance));
    return balance >= 0 ? `+₩${formatted}` : `-₩${formatted}`;
  }
</script>

<ReceiptPageLayout>
  {#snippet header()}
    <ReceiptCard variant="full">
      <ReceiptHeader
        title="SETTLEMENT"
        subtitle={trip.name}
        receiptNo="STL-{trip.id}"
        date={today}
      />
    </ReceiptCard>
  {/snippet}

  {#snippet content()}
    <ReceiptCard variant="full">
      <div class="settlement-body">
        <!-- SUMMARY -->
        <ReceiptLabel text="SUMMARY" />
        <ReceiptItemRow name="총 지출" amount={toDisplay(settlement.totalExpenses)} currency="₩" />
        <div class="custom-row">
          <span class="custom-row__name">참여자</span>
          <span class="custom-row__dots"></span>
          <span class="custom-row__value">{participantCount}명</span>
        </div>
        <div class="custom-row custom-row--highlight">
          <span class="custom-row__name">1인당</span>
          <span class="custom-row__dots"></span>
          <span class="custom-row__value">₩{fmtAmount(settlement.perPersonShare)}</span>
        </div>

        <ReceiptDivider variant="dashed" spacing="md" />

        <!-- MY STATUS -->
        <ReceiptLabel text="MY STATUS" />
        <div class="my-status-row">
          <span class="my-status-label">내가 낼 금액</span>
          <span class="my-status-dots"></span>
          <span class="my-status-amount">₩0</span>
        </div>
        <div class="my-status-row">
          <span class="my-status-label">받을 금액</span>
          <span class="my-status-dots"></span>
          <span class="my-status-amount my-status-amount--receive">₩{fmtAmount(settlement.participants[0]?.balance || 0)}</span>
        </div>

        <ReceiptDivider variant="double" spacing="md" />

        <!-- TRANSFERS -->
        <ReceiptLabel text="TRANSFERS" />
        {#each settlement.transactions as tx}
          <div class="transfer-row">
            <span class="transfer-row__names">
              {tx.from.name} <span class="transfer-row__arrow">→</span> {tx.to.name}
            </span>
            <span class="transfer-row__dots"></span>
            <span class="transfer-row__amount">₩{fmtAmount(tx.amount)}</span>
          </div>
        {/each}

        <!-- SETTLED STAMP -->
        {#if settlement.settled}
          <div class="stamp-container">
            <ReceiptStamp text="SETTLED" color="teal" rotation={-12} />
          </div>
        {/if}

        <!-- FOOTER -->
        <ReceiptFooter
          lines={[`TRIP: ${trip.name}`, `SETTLED: ${settlement.settledAt || today}`]}
          showBarcode={true}
          timestamp={settlement.settledAt || today}
        />
      </div>
    </ReceiptCard>
  {/snippet}
</ReceiptPageLayout>

<style>
  .settlement-body {
    position: relative;
    padding: 4px 0;
  }

  /* Custom row for non-currency amounts (e.g. "4명") */
  .custom-row {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-primary);
    height: 28px;
    line-height: 28px;
  }

  .custom-row--highlight {
    font-weight: 700;
  }

  .custom-row--highlight .custom-row__value {
    color: var(--receipt-text-accent, #20B2AA);
  }

  .custom-row__name {
    flex-shrink: 0;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .custom-row__dots {
    flex: 1;
    min-width: 12px;
    border-bottom: 1px dotted var(--receipt-divider);
    margin-bottom: 4px;
  }

  .custom-row__value {
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--receipt-ls-amount, 0.5px);
    white-space: nowrap;
    flex-shrink: 0;
  }


  /* Transfer rows — names default, amount accent */
  .transfer-row {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-primary);
    font-weight: 500;
    height: 28px;
    line-height: 28px;
  }

  .transfer-row__names {
    flex-shrink: 0;
    max-width: 65%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .transfer-row__arrow {
    color: var(--receipt-text-muted);
    font-weight: 900;
  }

  .transfer-row__dots {
    flex: 1;
    min-width: 12px;
    border-bottom: 1px dotted var(--receipt-divider);
    margin-bottom: 4px;
  }

  .transfer-row__amount {
    font-variant-numeric: tabular-nums;
    letter-spacing: var(--receipt-ls-amount, 0.5px);
    white-space: nowrap;
    flex-shrink: 0;
    font-weight: 700;
    color: var(--receipt-text-accent, #20B2AA);
  }

  /* MY STATUS rows */
  .my-status-row {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    font-family: var(--font-mono);
    font-size: var(--receipt-size-body, 13px);
    color: var(--receipt-text-primary);
    height: 28px;
    line-height: 28px;
  }

  .my-status-label { flex-shrink: 0; }
  .my-status-dots {
    flex: 1;
    min-width: 12px;
    border-bottom: 1px dotted var(--receipt-divider);
    margin-bottom: 4px;
  }
  .my-status-amount {
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .my-status-amount--receive { color: #34C759; }
  .my-status-amount--pay { color: #FF3B30; }

  /* Stamp positioning container */
  .stamp-container {
    position: relative;
    height: 80px;
    margin: 16px 0;
  }
</style>
